import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons';
import Text from 'js/components/text/text';
import Modal from 'js/components/modal/modal';
import Button from 'js/components/button/button';
import Row from 'js/components/grid/row';
import Col from 'js/components/grid/column';
import Input from 'js/components/input/input';
import TextArea from 'js/components/textarea/textarea';
import Box from 'js/components/box/box';
import Spinner from 'js/components/spinner/spinner';
import { quickStartApi, validEmail } from 'js/utils';
import styles from './need-help-modal.module.scss';

const initialFormData = {
    message: '',
    email: '',
};

const NeedHelpModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessMsg, setShowSuccessMsg] = useState(false);
    const [hasApiError, setHasApiError] = useState(false);
    const [validationError, setValidationError] = useState(false);

    const toggleModal = () => {
        setFormData(initialFormData);
        setShowSuccessMsg(false);
        setHasApiError(false);
        setValidationError(false);
        setShowModal((current) => !current);
    };

    const onSubmit = async () => {
        if (!validEmail(formData.email)) {
            setValidationError(true);
            return;
        }

        setIsLoading(true);
        setHasApiError(false);

        try {
            await quickStartApi().help(formData);
            setShowSuccessMsg(true);
        } catch (err) {
            setHasApiError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const title = showSuccessMsg ? 'Message Received!' : "We're Here to Help";

    const actionItems = (
        <Row>
            <Col span="auto">
                <Button
                    theme={showSuccessMsg ? 'primary' : 'outline'}
                    onClick={() => toggleModal()}
                >
                    Cancel
                </Button>
            </Col>
            {!showSuccessMsg && (
                <Col span="auto">
                    <Button
                        onClick={onSubmit}
                        disabled={
                            !formData.email ||
                            !formData.message.trim() ||
                            isLoading ||
                            showSuccessMsg
                        }
                    >
                        {isLoading ? <Spinner /> : 'Submit Message'}
                    </Button>
                </Col>
            )}
        </Row>
    );

    return (
        <>
            <div
                className={styles['center-row']}
                onClick={toggleModal}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        toggleModal();
                    }
                }}
            >
                <Text color={['gray', 'white']} size="large" inline>
                    <Text size="huge" inline>
                        <FontAwesomeIcon icon={faQuestionCircle} />
                    </Text>
                </Text>{' '}
                <Text
                    color={['gray', 'white']}
                    size="large"
                    textDecorationLine="underline"
                    inline
                >
                    Need Help?
                </Text>
            </div>
            {showModal && (
                <Modal title={title} actionItems={actionItems} preventClosing>
                    {showSuccessMsg ? (
                        <Box margin={['none', 'none', 'base', 'none']}>
                            <Text color={['gray', 'dark']}>
                                Your request for help has been received on our
                                end. We will reach out to you via email as soon
                                as possible.
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Box margin={['none', 'none', 'base', 'none']}>
                                <Text color={['gray', 'dark']}>
                                    Help is on the way! Well… first you need to
                                    type some stuff. But then it’s on the way.
                                </Text>
                            </Box>
                            <Box margin={['none', 'none', 'base', 'none']}>
                                <TextArea
                                    value={formData.message}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            message: e.target.value,
                                        })
                                    }
                                    maxLength={1000}
                                />
                            </Box>
                            <Input
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        email: e.target.value,
                                    })
                                }
                                label="Your Email"
                                hasError={validationError}
                                errorMessage={
                                    <Text color={['red', 'base']}>
                                        Please enter a valid email address.
                                    </Text>
                                }
                            />
                            {hasApiError && (
                                <Box margin={['base', 0, 0, 0]}>
                                    <Text color={['red', 'base']}>
                                        An unexpected error occured, please try
                                        again later.
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                </Modal>
            )}
        </>
    );
};

export default NeedHelpModal;
